/**
 * api.ts — Camada de acesso do Studio
 *
 * Todo acesso a dados passa por RPCs `SECURITY DEFINER`; nunca há SELECT direto
 * em tabelas a partir do navegador.
 *
 * RELEASE: v1.2.0 - 2026-09-10 - Consulta dos envios de formulário das páginas
 *                                públicas (busca, paginação e visitas)
 * RELEASE: v1.1.0 - 2026-09-10 - Páginas públicas: dados de publicação na listagem
 *                                e no detalhe + publicação da página pela função
 *                                `studio-publish-page` (compila, saneia e publica)
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 1)
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type {
  StudioDocument,
  StudioTemplateType,
  StudioOutput,
} from './core';

/** Bucket privado dos arquivos usados nos modelos. */
export const STUDIO_ASSETS_BUCKET = 'studio-assets';

/** Item de modelo exibido na listagem. */
export interface StudioTemplateListItem {
  id: string;
  name: string;
  slug: string;
  type: StudioTemplateType;
  status: 'draft' | 'published' | 'archived';
  description: string | null;
  default_output_modes: StudioOutput[];
  current_draft_version_id: string | null;
  current_published_version_id: string | null;
  published_version_number: number | null;
  versions_count: number;
  publication_status: StudioPublicationStatus | null;
  publication_path: string | null;
  publication_hostname: string | null;
  updated_at: string;
  total_count: number;
}

/** Situação da publicação de uma página pública. */
export type StudioPublicationStatus = 'draft' | 'published' | 'unpublished' | 'archived';

/** Dados de SEO da página pública. */
export interface StudioPageSeo {
  title?: string | null;
  description?: string | null;
  image_url?: string | null;
  canonical_url?: string | null;
  indexable?: boolean;
}

/** Publicação de página pública (endereço + versão no ar). */
export interface StudioPublication {
  id: string;
  template_id: string;
  published_version_id: string | null;
  hostname: string | null;
  path: string;
  status: StudioPublicationStatus;
  seo_json: StudioPageSeo;
  compiled_hash: string | null;
  compiler_version: string | null;
  views_count: number;
  published_at: string | null;
  unpublished_at: string | null;
}

/** Versão de modelo retornada pelo detalhe. */
export interface StudioTemplateVersion {
  id: string;
  template_id: string;
  version_number: number;
  status: 'draft' | 'published' | 'archived';
  source_format: 'blocks' | 'raw_html';
  schema_version: number;
  source_content: StudioDocument | Record<string, unknown>;
  theme_json: Record<string, unknown>;
  settings_json: Record<string, unknown>;
  compiled_html: string | null;
  compiled_css: string | null;
  compiler_version: string | null;
  published_at: string | null;
  created_at: string;
}

/** Detalhe completo de um modelo. */
export interface StudioTemplateDetail {
  template: Omit<StudioTemplateListItem, 'total_count' | 'versions_count' | 'published_version_number'>;
  draft_version: StudioTemplateVersion | null;
  published_version: StudioTemplateVersion | null;
  /** Publicação da página pública (somente para modelos do tipo página). */
  publication?: StudioPublication | null;
  versions: Array<{
    id: string;
    version_number: number;
    status: string;
    published_at: string | null;
    created_at: string;
  }>;
}

/** Filtros da listagem de modelos. */
export interface StudioTemplateFilters {
  search?: string | null;
  type?: StudioTemplateType | null;
  status?: 'draft' | 'published' | 'archived' | null;
  limit?: number;
  offset?: number;
}

/** Lista os modelos da empresa. */
export async function listTemplates(
  filtros: StudioTemplateFilters = {},
): Promise<{ items: StudioTemplateListItem[]; total: number }> {
  const { data, error } = await supabase.rpc('studio_list_templates', {
    p_search: filtros.search ?? null,
    p_type: filtros.type ?? null,
    p_status: filtros.status ?? null,
    p_limit: filtros.limit ?? 50,
    p_offset: filtros.offset ?? 0,
  });

  if (error) {
    logger.error('studio', 'Falha ao listar modelos', error);
    throw error;
  }

  const itens = (data ?? []) as unknown as StudioTemplateListItem[];
  return { items: itens, total: itens[0]?.total_count ?? 0 };
}

/** Abre um modelo com rascunho, versão publicada e histórico. */
export async function getTemplate(templateId: string): Promise<StudioTemplateDetail> {
  const { data, error } = await supabase.rpc('studio_get_template', {
    p_template_id: templateId,
  });

  if (error) {
    logger.error('studio', 'Falha ao abrir modelo', error);
    throw error;
  }

  return data as unknown as StudioTemplateDetail;
}

/** Dados de gravação do rascunho. */
export interface StudioDraftInput {
  templateId?: string | null;
  name: string;
  type: StudioTemplateType;
  description?: string | null;
  slug?: string | null;
  sourceFormat?: 'blocks' | 'raw_html';
  document: StudioDocument;
  outputModes?: StudioOutput[];
}

/** Cria ou atualiza a versão em rascunho do modelo. */
export async function upsertDraft(
  entrada: StudioDraftInput,
): Promise<{ template_id: string; draft_version_id: string }> {
  const { data, error } = await supabase.rpc('studio_upsert_draft', {
    p_name: entrada.name,
    p_type: entrada.type,
    p_source_content: entrada.document as unknown as never,
    p_template_id: entrada.templateId ?? null,
    p_slug: entrada.slug ?? null,
    p_description: entrada.description ?? null,
    p_source_format: entrada.sourceFormat ?? 'blocks',
    p_schema_version: entrada.document.schemaVersion,
    p_theme_json: (entrada.document.theme ?? {}) as unknown as never,
    p_settings_json: (entrada.document.settings ?? {}) as unknown as never,
    p_output_modes: entrada.outputModes ?? ['web'],
  });

  if (error) {
    logger.error('studio', 'Falha ao salvar rascunho', error);
    throw error;
  }

  return data as unknown as { template_id: string; draft_version_id: string };
}

/** Publica o rascunho atual (ou uma versão específica). */
export async function publishVersion(
  templateId: string,
  versionId?: string | null,
): Promise<{ template_id: string; published_version_id: string }> {
  const { data, error } = await supabase.rpc('studio_publish_version', {
    p_template_id: templateId,
    p_version_id: versionId ?? null,
  });

  if (error) {
    logger.error('studio', 'Falha ao publicar modelo', error);
    throw error;
  }

  return data as unknown as { template_id: string; published_version_id: string };
}

/** Entrada da publicação de uma página pública. */
export interface StudioPublishPageInput {
  templateId: string;
  versionId?: string | null;
  /** Endereço público (sem barras nas pontas), ex.: `promo-frota`. */
  path: string;
  hostname?: string | null;
  seo?: StudioPageSeo;
}

/**
 * Publica uma página pública.
 *
 * A compilação, o saneamento e o SEO são feitos no servidor pela função
 * `studio-publish-page` — o navegador nunca envia o HTML final.
 */
export async function publishPage(entrada: StudioPublishPageInput): Promise<{
  template_id: string;
  published_version_id: string;
  publication_id: string | null;
  path: string;
  hash: string;
}> {
  const { data, error } = await supabase.functions.invoke('studio-publish-page', {
    body: {
      template_id: entrada.templateId,
      version_id: entrada.versionId ?? null,
      path: entrada.path,
      hostname: entrada.hostname ?? null,
      seo: entrada.seo ?? {},
    },
  });

  if (error) {
    logger.error('studio', 'Falha ao publicar página pública', error);

    // A função devolve 422 quando há imagens indisponíveis. Nesse caso o corpo
    // traz a lista de pendências, que precisa chegar ao usuário.
    const contexto = (error as { context?: { text?: () => Promise<string> } })?.context;
    if (contexto?.text) {
      try {
        const corpo = JSON.parse(await contexto.text()) as {
          error?: string;
          pending_assets?: string[];
        };
        if (corpo?.error) {
          const detalhes = Array.isArray(corpo.pending_assets) && corpo.pending_assets.length
            ? `\n\n• ${corpo.pending_assets.join('\n• ')}`
            : '';
          throw new Error(`${corpo.error}${detalhes}`);
        }
      } catch (erroLeitura) {
        if (erroLeitura instanceof Error && erroLeitura.message) throw erroLeitura;
      }
    }

    throw error;
  }

  if ((data as { error?: string })?.error) {
    throw new Error((data as { error: string }).error);
  }

  return data as {
    template_id: string;
    published_version_id: string;
    publication_id: string | null;
    path: string;
    hash: string;
    public_url?: string;
  };
}

/**
 * Gera um link temporário de pré-visualização da página (15 minutos).
 *
 * Nada é publicado: o servidor apenas assina um endereço que mostra o rascunho
 * atual, sempre fora de buscas.
 */
export async function previewPage(templateId: string): Promise<{
  preview_url: string;
  expires_at: string;
}> {
  const { data, error } = await supabase.functions.invoke('studio-publish-page', {
    body: { template_id: templateId, mode: 'preview' },
  });

  if (error) {
    logger.error('studio', 'Falha ao gerar pré-visualização da página', error);
    throw error;
  }

  if ((data as { error?: string })?.error) {
    throw new Error((data as { error: string }).error);
  }

  return data as { preview_url: string; expires_at: string };
}

/**
 * Monta o endereço público de uma página já publicada.
 *
 * A exibição acontece no domínio do aplicativo (rota `/lp/<caminho>`): o
 * endereço técnico da função entrega o conteúdo como texto simples, então
 * quem apresenta a página é o app, buscando o HTML na função. Aceita caminhos
 * com subpastas (ex.: `campanhas/promo-frota`).
 */
export function buildPublicPageUrl(path: string): string {
  const caminho = String(path ?? '').replace(/^\/+|\/+$/g, '');
  return `${window.location.origin}/lp/${caminho}`;
}

/* -------------------------------------------------------------------------
 * Envios de formulário das páginas públicas (acompanhamento)
 * ---------------------------------------------------------------------- */

/** Um envio de formulário recebido por uma página pública. */
export interface StudioPageSubmission {
  id: string;
  form_id: string;
  data_json: Record<string, unknown>;
  attribution_json: Record<string, unknown>;
  contact_id: string | null;
  contact_name: string | null;
  template_version_id: string | null;
  version_number: number | null;
  user_agent: string | null;
  created_at: string;
  total_count: number;
  views_count: number;
}

/** Filtros da listagem de envios. */
export interface StudioSubmissionFilters {
  templateId: string;
  search?: string | null;
  limit?: number;
  offset?: number;
}

/**
 * Lista os envios de formulário de uma página pública.
 * Traz também o total de visitas acumulado da publicação.
 */
export async function listPageSubmissions(filtros: StudioSubmissionFilters): Promise<{
  items: StudioPageSubmission[];
  total: number;
  views: number;
}> {
  const { data, error } = await supabase.rpc('studio_list_page_submissions', {
    p_template_id: filtros.templateId,
    p_search: filtros.search ?? null,
    p_limit: filtros.limit ?? 20,
    p_offset: filtros.offset ?? 0,
  });

  if (error) {
    logger.error('studio', 'Falha ao listar envios da página', error);
    throw error;
  }

  const itens = (data ?? []) as unknown as StudioPageSubmission[];
  return {
    items: itens,
    total: Number(itens[0]?.total_count ?? 0),
    views: Number(itens[0]?.views_count ?? 0),
  };
}
